import React, { useContext, useEffect, useState } from "react";
import {
  getAppointments,
  updateAppointmentStatus,
} from "../API/AppointmentsApi";
import AuthContext from "../Context/AuthProvider";
import {
  convertUTCToTimeZone,
  convertUTCToTimeZoneDateTimeString,
} from "../Utility/DateTimeUtility";
import { AppointmentStatus, DateRangeOptions } from "../Utility/Constants";
import { Modal } from "bootstrap";
import { getDisplayValue } from "../Utility/Utility";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Grid2,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Card,
  CardContent,
  CardActions,
  Divider,
  useMediaQuery,
  useTheme,
  CardHeader,
} from "@mui/material";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import MedicationIcon from "@mui/icons-material/Medication";
import LocalParkingRoundedIcon from "@mui/icons-material/LocalParkingRounded";
import MoreVert from '@mui/icons-material/MoreVert';
import DateRangeDialog from "./ChangeDates";
import dayjs from "dayjs";
import TodayQueue from "./TodayQueue";
import { getVisits } from "../API/VisitsApi";
import { useSnackbar } from "../Utility/UserMessageContext";
import InitialVisitInfo from "./InitialVisitInfo";
import ChangeVisitDialog from "./ChangeVisitDialog";
import { formatAge } from "../Utility/Utility";
import MenuBar from "../common/MenuBar";

function AppointmentListNew1({
  onViewDetails,
  onAddNewAppointment,
  onAddNewPrescription,
  onEditVisit,
  onViewPatientDetails,
}) {
  const { auth } = useContext(AuthContext);
  const [allAppointments, setAllAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState();
  const [newStatus, setNewStatus] = useState();
  const [showSpinner, setShowSpinner] = useState(false);
  const checkInPatientModalId = "checkIn" + selectedAppointment?.id;
  const [showQueue, setShowQueue] = useState();
  const appointmentStatusList = [
    "Scheduled",
    "CheckedIn",
    "InProgress",
    "Cancelled",
    "Completed",
    "NoShow",
  ];
  const { showSnackbar } = useSnackbar();
  // Theme for responsive design
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [keyForRescheduleRendering, setKeyForRescheduleRendering] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [openDateRangeDialog, setOpenDateRangeDialog] = useState(false);
  const [openVisitInfoDialog, setOpenVisitInfoDialog] = useState(false);
  const [openEditVisitDialog, setOpenEditVisitDialog] = useState(false);
  
  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setCurrentRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setCurrentRow(null);
  };

  const [filters, setFilters] = useState({
    from: dayjs(new Date()),
    to: dayjs(new Date()).add(7, "days"),
    selectedDateRangeOption: DateRangeOptions.Next7Days,
    statusList: appointmentStatusList,
  });

  useEffect(() => {
    const fetchData = async () => {
      setShowSpinner(true);
      var result = await getVisits(auth, filters);
      if (result.isSuccess) {
        setAllAppointments(result?.data);
      } else {
        showSnackbar(result?.data, "error");
      }
      setShowSpinner(false);
    };

    fetchData();
  }, [auth, filters]);

  const [pageSize, setPageSize] = useState(5);

  const showStatusUpdateModal = (newStatus) => {
    const modalElement = document.getElementById(checkInPatientModalId);
    if (modalElement) {
      setNewStatus(newStatus);
      const modal = new Modal(modalElement, {});
      modal.show();
      handleMenuClose();
    }
  };

  const rescheduleAppointment = () => {
    onEditVisit(selectedAppointment);
    handleMenuClose();
  };

  const resetRescheduleAppointmentForm = () => {
    setKeyForRescheduleRendering((prevKey) => prevKey + 1);
  };

  const onStatusChanged = async () => {
    var result = await updateAppointmentStatus(
      auth,
      selectedAppointment?.id,
      newStatus
    );
    if (result.isSuccess) {
      const modal = Modal.getOrCreateInstance("#" + checkInPatientModalId);
      modal.hide();
      refreshList();
    } else {
      showSnackbar(
        "Error occurred while changing the status of the appointment.",
        "error"
      );
    }
  };

  const onRowClick = async (e) => {
    setSelectedAppointment(e?.row);
  };

  const refreshList = async () => {
    setShowSpinner(true);
    var result = await getAppointments(auth, filters);

    if (result.isSuccess) {
      setAllAppointments(result?.data);
    }

    setShowSpinner(false);
  };

  const columns = [
    {
      field: "displayId",
      headerName: "ID",
      width: 230,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span
            style={{
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={() => onViewDetails(params.row.id, params.row.displayId)}
          >
            {params.row.displayId}
          </span>
          {params.row.planId && (
            <Tooltip title="Linked to Plan" arrow>
              
                <LocalParkingRoundedIcon fontSize="4" />
            </Tooltip>
          )}

          {/* Three dots menu for all actions */}
          <Tooltip title="Actions" arrow>
            <IconButton
              size="small"
              sx={{ marginLeft: "auto" }}
              onClick={(event) => handleMenuOpen(event, params.row)}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    // {
    //   field: "patient",
    //   type: "text",
    //   headerName: "Patient",
    //   width: 180,
    //   renderCell: (params) =>
    //     params?.row?.patientPrefix +
    //     " " +
    //     params?.row?.patientFirstName +
    //     " " +
    //     params?.row?.patientLastName,
    // },
    {
      field: "patient",
      type: "text",
      headerName: "Patient",
      width: 230,
      renderCell: (params) => {
        const patientPrefix = params.row.patientPrefix || "";
        const firstName = params.row.patientFirstName || "";
        const lastName = params.row.patientLastName || "";
        const gender = params.row.patientGender || "";
        const age = params.row.patientAgeInYears || "";
        const phone = params.row.patientMobileNo || "";
        const countryCode = params?.row?.countryCodeForPhone || "";

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
              lineHeight: 1.3,
            }}
          >
            <div>
              <span
                style={{
                  fontWeight: "bold",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => {
                  const patientData = {
                    id: params.row.patientId,
                    firstName: params.row.patientFirstName,
                    lastName: params.row.patientLastName,
                    prefix: params.row.patientPrefix,
                    gender: params.row.patientGender,
                    ageInYears: params.row.patientAgeInYears,
                    mobileNo: params.row.patientMobileNo,
                    emailId: params.row.patientEmailId,
                  };
                  onViewPatientDetails && onViewPatientDetails(patientData);
                }}
              >
                {patientPrefix} {firstName} {lastName}
              </span>
              <span style={{ fontWeight: "normal" }}> ({gender})</span>
            </div>

            <span style={{ color: "#555", fontSize: "0.85em" }}>
              {`${countryCode} ${phone} (${formatAge(age)})`}
            </span>
          </div>
        );
      },
    },
    {
      field: "physician",
      headerName: "Physician",
      width: 200,
      renderCell: (params) =>
        params?.row?.physicianPrefix +
        " " +
        params?.row?.physicianFirstName +
        " " +
        params?.row?.physicianLastName,
    },
    {
      field: "service",
      headerName: "Primary Service",
      width: 180,
      renderCell: (params) => params?.row?.primaryServiceName,
    },
    {
      field: "currentVisitType",
      headerName: "Visit Type",
      width: 150,
      renderCell: (params) => getDisplayValue(params?.row?.currentVisitType),
    },
    {
      field: "scheduledStartDateTime",
      headerName: "Visit Time",
      width: 220,
      renderCell: (params) =>
        convertUTCToTimeZoneDateTimeString(
          params?.row?.scheduledStartDateTime,
          auth?.selectedFacility?.timeZone,
        ),
    },
    {
      field: "visitStatus",
      headerName: "Status",
      width: 180,
      renderCell: (params) => getDisplayValue(params?.row?.status),
    },
  ];

  // Action buttons for card view
  const renderCardActions = (appointment) => {
    return (
      <CardActions sx={{ justifyContent: "flex-end", px: 1, pb: 1 }}>
        <Button
          title="View Details"
          onClick={() => onViewDetails(appointment)}
          variant="outlined"
          size="small"
          startIcon={<EventNoteOutlinedIcon />}
        ></Button>

        {appointment.status !== AppointmentStatus.Completed && (
          <Button
            title="Change Appointment"
            onClick={(event) => {
              setSelectedAppointment(appointment);
              handleMenuOpen(event, appointment);
            }}
            size="small"
            variant="outlined"
            startIcon={<CreateOutlinedIcon />}
            color="secondary"
          ></Button>
        )}

        {appointment.status === AppointmentStatus.InProgress && (
          <Button
            title="New Prescription"
            onClick={() => onAddNewPrescription(appointment?.id)}
            size="small"
            variant="outlined"
            startIcon={<MedicationIcon />}
            color="success"
          >
            Prescription
          </Button>
        )}
      </CardActions>
    );
  };

  // Card view for small screens
  const renderCardView = () => {
    return (
      <Grid2 sx={{ px: 1 }}>
        {showSpinner ? (
          <Typography variant="body1" align="center" py={3}>
            Loading...
          </Typography>
        ) : allAppointments.length === 0 ? (
          <Typography variant="body1" align="center" py={3}>
            There are no visits to display. Change the dates or create a new
            visit.
          </Typography>
        ) : (
          allAppointments.map((appointment) => (
            <Card
              item
              xs={12}
              key={appointment.id}
              variant="outlined"
              sx={{ mb: 2, borderRadius: 2 }}
              onClick={() => setSelectedAppointment(appointment)}
            >
              <CardHeader 
                title={appointment?.displayId} 
                action={
                  appointment.planId && (
                    <Tooltip title="Linked to Plan" arrow>
                      <LocalParkingRoundedIcon fontSize="small" />
                    </Tooltip>
                  )
                }
              />
              <Divider sx={{ mt: 2 }} />
              <CardContent>
                <Typography variant="body2">
                  {appointment.primaryServiceName}
                </Typography>
                <Typography variant="body2">
                  {`${appointment.patientPrefix || ""} ${
                    appointment.patientFirstName || ""
                  } ${appointment.patientLastName || ""}`}
                </Typography>
                <Typography variant="body2">
                  Gender: {appointment.patientGender || "-"}
                </Typography>
                <Typography variant="body2">
                  Age: {formatAge(appointment.patientAgeInYears)}
                </Typography>
                <Typography variant="body2">
                  Contact: {appointment.patientMobileNo || "-"}
                </Typography>
                <Typography variant="body2">
                  Scheduled:{" "}
                  {appointment.scheduledStartDateTime &&
                   convertUTCToTimeZoneDateTimeString(
                      appointment.scheduledStartDateTime,
                      auth?.selectedFacility?.timeZone,
                    )}
                </Typography>
                <Typography variant="body2">
                  Status: {getDisplayValue(appointment?.visitStatus) || "-"}
                </Typography>

                <CardActions sx={{ justifyContent: "flex-end" }}>
                  <Tooltip title="View Details">
                    <IconButton
                      onClick={() => onViewDetails(appointment)}
                      size="small"
                    >
                      <EventNoteOutlinedIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Actions">
                    <IconButton
                      onClick={(event) => {
                        setSelectedAppointment(appointment);
                        handleMenuOpen(event, appointment);
                      }}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </CardContent>
            </Card>
          ))
        )}
      </Grid2>
    );
  };

  return (
    <>
      <Grid2 container>
        {/* Appointment Actions and Date Display */}
        <Grid2 container size={12}>
          <Grid2 size={12}>
            <MenuBar
              showChangeDate={true}
              onChangeDate={() => setOpenDateRangeDialog(true)}
              showNew={true}
              onNew={() => setOpenVisitInfoDialog(true)}
              newLabel="New Visit"
              newTooltip="New Visit"
              showTodaysQueue={true}
              onShowQueue={() => setShowQueue(!showQueue)}
              disabled={false}
            />
          </Grid2>
          {/* Date Range Display */}
          <Grid2 size={12} sx={{ px: 1, py: 1 }}>
            <Typography variant="body1">
              Showing appointments from {filters?.from?.format("DD/MM/YYYY")} to{" "}
              {filters?.to?.format("DD/MM/YYYY")}
            </Typography>
          </Grid2>
        </Grid2>
        {(!isSmallScreen || !showQueue) && (
          <Box
            sx={{
              width: isSmallScreen ? "100%" : showQueue ? "80%" : "100%",
              transition: "width 0.3s ease",
              paddingX: "5px",
              height: isSmallScreen ? "auto" : 600,
              maxHeight: "calc(100vh - 180px)",
              overflowY: "auto",
            }}
          >
            {isSmallScreen ? (
              renderCardView()
            ) : (
              <DataGrid
                localeText={{
                  noRowsLabel:
                    "There are no visits to display. Change the dates or create a new visit.",
                }}
                loading={showSpinner}
                slotProps={{
                  loadingOverlay: {
                    variant: "skeleton",
                    noRowsVariant: "skeleton",
                  },
                }}
                initialState={{
                  density: "compact",
                  pagination: {
                    paginationModel: { pageSize: 5 },
                  },
                }}
                sx={{
                  width: "100%",
                  "& .MuiTablePagination-toolbar": {
                    minHeight: "unset",
                    alignItems: "center",
                    paddingTop: "0 !important",
                    paddingBottom: "0 !important",
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      margin: 0,
                    },
                }}
                rows={allAppointments}
                columns={columns}
                pageSizeOptions={[5, 10, 15]}
                rowsPerPageOptions={[5, 10, 15]}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                onRowClick={onRowClick}
              />
            )}

            {/* Updated Menu with all actions */}
            <Menu
  anchorEl={menuAnchor}
  open={Boolean(menuAnchor)}
  onClose={handleMenuClose}
>
  {currentRow && (
    <>
      {/* Reschedule */}
      {(currentRow.visitStatus === AppointmentStatus.Cancelled ||
        currentRow.visitStatus === AppointmentStatus.CheckedIn ||
        currentRow.visitStatus === AppointmentStatus.Scheduled) && (
        <MenuItem onClick={rescheduleAppointment}>
          Reschedule
        </MenuItem>
      )}

      {/* Check In */}
      {currentRow.visitStatus === AppointmentStatus.Scheduled &&
        convertUTCToTimeZone(
          currentRow.visitDateTime,
          auth?.selectedFacility?.timeZone
        ).format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD") && (
          <MenuItem
            onClick={() =>
              showStatusUpdateModal(AppointmentStatus.CheckedIn)
            }
          >
            Check In
          </MenuItem>
        )}

      {/* Cancel */}
      {(currentRow.visitStatus === AppointmentStatus.Scheduled ||
        currentRow.visitStatus === AppointmentStatus.CheckedIn) && (
        <MenuItem
          onClick={() =>
            showStatusUpdateModal(AppointmentStatus.Cancelled)
          }
        >
          Cancel
        </MenuItem>
      )}

      {/* Complete */}
      {currentRow.visitStatus === AppointmentStatus.InProgress && (
        <MenuItem
          onClick={() =>
            showStatusUpdateModal(AppointmentStatus.Completed)
          }
        >
          Complete
        </MenuItem>
      )}
      {/* No Show */}
      {currentRow.visitStatus === AppointmentStatus.Scheduled &&
        convertUTCToTimeZone(
          currentRow.visitDateTime,
          auth?.selectedFacility?.timeZone
        ).isBefore(dayjs()) && (
          <MenuItem
            onClick={() =>
              showStatusUpdateModal(AppointmentStatus.NoShow)
            }
          >
            No Show
          </MenuItem>
        )}
    </>
  )}
</Menu>
          </Box>
        )}
        {!isSmallScreen && (
          <Box
            sx={{
              display: "flex",
              width: showQueue ? "20%" : "0",
              transition: "width 1s ease",
              justifyContent: "flex-end",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: 500,
                width: showQueue ? "100%" : "0",
                transition: "width 1s ease",
              }}
            >
              <TodayQueue onClose={() => setShowQueue(false)} />
            </Box>
          </Box>
        )}
        <Grid2 sx={{ maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
          {isSmallScreen && showQueue && (
            <Box
              sx={{
                width: isSmallScreen ? "100%" : "20%",
                height: isSmallScreen ? "auto" : 500,
                paddingX: "5px",
                ml: isSmallScreen ? 0 : "5px",
                transition: "all 0.3s ease",
              }}
            >
              <TodayQueue onClose={() => setShowQueue(false)} />
            </Box>
          )}
        </Grid2>
      </Grid2>
      <div class="container-fluid">
        <div class="row justify-content-center align-items-center mt-4">
          <div
            class="modal"
            tabindex="-1"
            id={checkInPatientModalId}
            data-bs-backdrop="static"
            data-bs-keyboard="false"
          >
            <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Change Status</h5>
                </div>
                <div class="modal-body">
                  <p>
                    You are changing the status of the appointment from{" "}
                    {selectedAppointment?.status} to {newStatus} for the patient
                    - {selectedAppointment?.patient?.firstName}. Are you sure?
                  </p>
                </div>
                <div class="modal-footer">
                  <button
                    type="button"
                    class="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    onClick={onStatusChanged}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div>
            <ChangeVisitDialog
              key={keyForRescheduleRendering}
              refreshAppointments={refreshList}
              reset={resetRescheduleAppointmentForm}
              visit={selectedAppointment}
              openState={openEditVisitDialog}
              setOpenState={setOpenEditVisitDialog}
            ></ChangeVisitDialog>
          </div>
          <DateRangeDialog
            key="visits"
            title="Show Visits For"
            filters={filters}
            setFilters={setFilters}
            openState={openDateRangeDialog}
            setOpenState={setOpenDateRangeDialog}
          ></DateRangeDialog>
          <InitialVisitInfo
            key="visits1"
            openState={openVisitInfoDialog}
            setOpenState={setOpenVisitInfoDialog}
            onApply={onAddNewAppointment}
          ></InitialVisitInfo>
        </div>
      </div>
    </>
  );
}

export default AppointmentListNew1;